import { NextSeoProps, OpenGraph } from 'next-seo/lib/types';

/**
 * Merges a page's SEO props over the app defaults.
 *
 * `{ ...defaultSEO, ...customSeoProps }` looks harmless and is not. A spread
 * replaces a nested object wholesale, so a page that set `openGraph` for its own
 * title and description silently dropped `images` along with it and emitted no
 * `og:image`, while `twitter.cardType` survived and went on promising
 * `summary_large_image`. That is the defect the venue showcases had, and
 * `/cerco-lavoro` had it too. Nothing about it is visible: the page renders, the
 * build is green, and the loss shows up only when somebody shares the link.
 *
 * Two rules, and the second one is the subtle half.
 *
 * A key the page did not set keeps the default. And a key the page set to
 * `undefined` also keeps the default, because the two are indistinguishable
 * here: `job-position-layout` writes `images: images.length > 0 ? images :
 * undefined`, and `showcase-v2` writes `additionalMetaTags: undefined` on the
 * branch with no image. A plain spread of either object writes `undefined` over
 * the default and undoes the whole fix.
 *
 * The rule is applied to every key rather than only to the ones that have bitten
 * us. `additionalMetaTags` has no default value today, so merging it changes
 * nothing right now; the point is that the day somebody adds one, it does not
 * quietly vanish on the pages that pass `undefined`, and nobody has to rediscover
 * why.
 */
export function mergeSeoProps(defaults: NextSeoProps, custom?: NextSeoProps): NextSeoProps {
  if (!custom) return defaults;

  return {
    ...defaults,
    ...definedOnly(custom),
    openGraph: mergeOpenGraph(defaults.openGraph, custom.openGraph),
  };
}

export function mergeOpenGraph(defaults?: OpenGraph, custom?: OpenGraph): OpenGraph | undefined {
  if (!defaults) return custom;
  if (!custom) return defaults;
  return { ...defaults, ...definedOnly(custom) };
}

/**
 * Built by filtering and then spreading, rather than by writing into a copy
 * through a computed key: indexing these types by their own `keyof` collapses
 * the value type to the intersection of every property, which is `never`, and an
 * assignment there cannot type-check. Jest runs through babel and never looks,
 * so that only shows up in `nx build`.
 */
function definedOnly<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}
